import React from 'react';
import { useAuth } from '../../../hooks/AuthProvider';
import { useLocations } from '../../../hooks/useLocations'; // ADD THIS IMPORT

type NavigationItem = 'app-management' | 'admin-panel' | 'permissions';

interface NavigationSidebarProps {
  selectedOrganizationId: string;
  activeItem: NavigationItem;
  onBackToOrganizations: () => void;
  onNavigateToApps: () => void;
  onNavigateToAdmin: () => void;
  onNavigateToPermissions?: () => void;
}

const NavigationSidebar: React.FC<NavigationSidebarProps> = ({
  selectedOrganizationId,
  activeItem,
  onBackToOrganizations,
  onNavigateToApps,
  onNavigateToAdmin,
  onNavigateToPermissions
}) => {
  const { userPermissions, hasPermission } = useAuth();
  const { getOrganizationName } = useLocations(); // ADD THIS LINE

  // UPDATED: Get organization name using the hook
  const getDisplayOrganizationName = (): string => {
    const orgName = getOrganizationName(selectedOrganizationId);
    // Your org names are in ALL CAPS, so you might want to format them nicely
    return formatOrganizationName(orgName);
  };

  // Helper function to format organization names nicely
  const formatOrganizationName = (name: string): string => {
    // Convert from "DINEUKFINALMILE" to "Dine UK Final Mile"
    // You can customize this logic based on your naming convention
    if (name === 'DINEUKFINALMILE') return 'Dine UK Final Mile';
    if (name === 'DINEUSA99') return 'Dine USA 99';
    
    // Generic formatting for other org names
    return name
      .toLowerCase()
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add spaces before capitals
      .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
  };

  // Check if user has admin access to this organization
  const hasAdminAccess = (): boolean => {
    return userPermissions.some(permission => 
      permission === `organizations_${selectedOrganizationId}_admin`
    );
  };

  // Check if user has DEPLOYMENT_ADMIN permission
  const hasDeploymentAdminAccess = (): boolean => {
    return hasPermission('DEPLOYMENT_ADMIN');
  };

  // Get organizations the user has access to
  const getUserOrganizations = (): string[] => {
    const organizations = new Set<string>();
    
    userPermissions.forEach(permission => {
      if (permission.startsWith('organizations_')) {
        const parts = permission.split('_');
        if (parts.length >= 3) {
          const orgId = parts[1];
          organizations.add(orgId);
        }
      }
    });

    return Array.from(organizations);
  };

  // Check if user has access to multiple organizations
  const hasMultipleOrganizations = (): boolean => {
    return getUserOrganizations().length > 1;
  };

  return (
    <div className="applist-sidebar">
      <div className="applist-sidebar-header">
        <h3 className="applist-sidebar-title">Administration</h3>
        <p className="applist-sidebar-subtitle">Manage your applications</p>
      </div>
      
      <div className="applist-sidebar-navigation">
        <div className="applist-nav-section">
          <div className="applist-nav-section-title">Navigation</div>
          
          {/* Only show "All Organizations" if user has access to multiple organizations */}
          {hasMultipleOrganizations() && (
            <button 
              type="button" 
              className="applist-nav-item"
              onClick={onBackToOrganizations}
            >
              <span className="applist-nav-icon">←</span>
              All Organizations
            </button>
          )}
          
          <button 
            type="button" 
            className={`applist-nav-item ${activeItem === 'app-management' ? 'applist-nav-item-active' : ''}`}
            onClick={onNavigateToApps}
          >
            <span className="applist-nav-icon">📱</span>
            App Management
          </button>
          
          {/* Only show admin panel if user has DEPLOYMENT_ADMIN permission */}
          {hasDeploymentAdminAccess() && (
            <button 
              type="button" 
              className={`applist-nav-item ${activeItem === 'admin-panel' ? 'applist-nav-item-active' : ''}`}
              onClick={onNavigateToAdmin}
            >
              <span className="applist-nav-icon">⚙️</span>
              Admin Panel
            </button>
          )}

          {/* Only show permissions if user has admin access and callback is provided */}
          {hasAdminAccess() && onNavigateToPermissions && (
            <button 
              type="button" 
              className={`applist-nav-item ${activeItem === 'permissions' ? 'applist-nav-item-active' : ''}`}
              onClick={onNavigateToPermissions}
            >
              <span className="applist-nav-icon">👥</span>
              Permissions
            </button>
          )}
        </div>
      </div>
      
      <div className="applist-organization-info">
        {/* UPDATED: Show formatted organization name instead of ID */}
        <h4>{getDisplayOrganizationName()}</h4>
        <p>Current Organization</p>
        
        {/* Only show "Change Organization" if user has access to multiple organizations */}
        {hasMultipleOrganizations() && (
          <button 
            type="button" 
            className="applist-change-org-btn"
            onClick={onBackToOrganizations}
          >
            Change Organization
          </button>
        )}
      </div>
    </div>
  );
};

export default NavigationSidebar;